import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pager, StatusPill } from '../components/ui';
import { date, isOverdue, likeEscape, money } from '../lib/format';
import { supabase } from '../lib/supabase';

interface QuoteRow {
  id: string; number: number; job_number: number | null; customer_name: string | null; contact_name: string | null;
  owner_name: string | null; created_at: string; reference: string | null; deadline: string | null; total: number;
  viewed_at: string | null; status: string | null; status_colour: string | null; accounting_status: string | null;
}

const VIEWS = {
  active: { label: 'Active', filter: (q: any) => q.not('status', 'in', '("Accepted","Declined")') },
  follow: { label: 'Follow up', filter: (q: any) => q.in('status', ['Follow up', 'Mockup checked']) },
  accepted: { label: 'Accepted', filter: (q: any) => q.eq('status', 'Accepted') },
  all: { label: 'All', filter: (q: any) => q },
} as const;
type View = keyof typeof VIEWS;
const PAGE = 50;

export default function Quotes() {
  const nav = useNavigate();
  const [view, setView] = useState<View>('active');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);
  const [rows, setRows] = useState<QuoteRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(async () => {
      setLoading(true);
      let query: any = supabase.from('quote_list').select('*', { count: 'exact' }).eq('archived', false)
        .order('number', { ascending: false }).range(page * PAGE, page * PAGE + PAGE - 1);
      query = VIEWS[view].filter(query);
      const s = q.trim();
      if (/^\d+$/.test(s)) query = query.eq('number', Number(s));
      else if (s) {
        const p = `%${likeEscape(s)}%`;
        query = query.or(`customer_name.ilike.${p},reference.ilike.${p},contact_name.ilike.${p}`);
      }
      const { data, count } = await query;
      setRows(data ?? []);
      setTotal(count ?? 0);
      setLoading(false);
    }, 200);
    return () => clearTimeout(t);
  }, [view, q, page]);

  const exportCsv = () => {
    const head = ['Quote #', 'Job #', 'Customer', 'Contact', 'Owner', 'Created', 'Reference', 'Deadline', 'Total', 'Status', 'Accounting'];
    const body = rows.map((r) => [r.number, r.job_number ?? '', r.customer_name ?? '', r.contact_name ?? '', r.owner_name ?? '',
      r.created_at?.slice(0, 10), r.reference ?? '', r.deadline ?? '', r.total, r.status ?? '', r.accounting_status ?? '']);
    const csv = [head, ...body].map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `quotes-${view}.csv`;
    a.click();
  };

  return (
    <div className="page">
      <div className="page-head">
        <h1>SalesBoard</h1>
        <input className="search" placeholder="Search quote #, customer or reference" value={q}
          onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        <span className="spacer" />
        <button onClick={exportCsv} disabled={!rows.length}>Export CSV</button>
        <button className="primary" onClick={() => nav('/quotes/new')}>+ New quote</button>
      </div>
      <div className="tabs">
        {(Object.keys(VIEWS) as View[]).map((v) => (
          <button key={v} className={view === v ? 'on' : ''} onClick={() => { setView(v); setPage(0); }}>
            {VIEWS[v].label}
          </button>
        ))}
      </div>
      <div className="card">
        <div className="table-wrap">
          <table className="grid">
            <thead>
              <tr>
                <th>#</th><th>Job</th><th>Customer</th><th>Owner</th><th>Created</th><th>Reference</th>
                <th>Deadline</th><th className="num">Total</th><th>Viewed</th><th>Status</th><th>Accounting</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="click" onClick={() => nav(`/quotes/${r.id}`)}>
                  <td><b>{r.number}</b></td>
                  <td>{r.job_number ?? ''}</td>
                  <td><div>{r.customer_name ?? <span className="muted">No customer</span>}</div>
                    {r.contact_name && <div className="muted small">{r.contact_name}</div>}</td>
                  <td>{r.owner_name}</td>
                  <td className="muted">{date(r.created_at)}</td>
                  <td className="wrap">{r.reference}</td>
                  <td className={isOverdue(r.deadline) ? 'overdue' : ''}>{date(r.deadline)}</td>
                  <td className="num">{money(r.total)}</td>
                  <td>{r.viewed_at ? '👁' : ''}</td>
                  <td><StatusPill label={r.status} colour={r.status_colour} /></td>
                  <td className="small muted">{r.accounting_status?.replace(/_/g, ' ').toLowerCase()}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loading && rows.length === 0 && <div className="empty">No quotes here.</div>}
          {loading && rows.length === 0 && <div className="empty">Loading…</div>}
        </div>
        <Pager page={page} pageSize={PAGE} total={total} onPage={setPage} />
      </div>
    </div>
  );
}
